// Gamification points toast.
//
// Every action in the admin's Gamification → Set Activity Points list (Profile
// Picture Upload, Post, Comment, Likes, Lesson Complete) tells the member what
// they just earned, in one consistent format:
//
//   "Posted. +45 points!"
//
// The amount always comes from the API's `points_awarded`, never from the
// gamification config the frontend already holds. That matters because the
// backend skips the award in cases the UI can't see — a post held for admin
// approval earns nothing until it's approved, and a lesson only pays out on the
// FIRST completion. Reading the config instead would confidently show "+45" for
// points that were never granted.
//
// ProfileModal.jsx toasts the profile-picture award inline against the same
// `points_awarded` field; this helper is the shared version for the feed and
// course surfaces.

import { toast } from "react-toastify";
import { bustCache } from "./apiCache";

/**
 * A group that UNLOCKS AT A LEVEL is gated on the member's point total, and the
 * groups list is served from a 30-second response cache — so for half a minute
 * after earning the points that qualify them, the member is still told "Reach
 * Bronze to unlock this group." Reported from production with 27 seconds
 * between the post that paid the points and the toast that refused the group.
 *
 * Nothing else can notice: `locked` is computed per request on the server and
 * the cached copy is indistinguishable from a fresh one. So every path that
 * moves the total drops the cached group reads, and the next look re-asks.
 *
 * The leaderboard and the rail are the member's other points-derived reads and
 * go with it, for the same reason.
 */
function forgetPointsDerivedReads() {
  bustCache("membership-groups");
  bustCache("membership-leaderboard");
  bustCache("membership-rail");
}

/**
 * Toast an action's outcome, appending the points earned when there are any.
 *
 * @param {number|undefined} awarded  `points_awarded` straight from the response
 * @param {string} message            what happened, e.g. "Posted."
 */
export function toastPoints(awarded, message) {
  const earned = Number(awarded || 0);
  if (earned !== 0) forgetPointsDerivedReads();
  // Only a positive award is worth announcing. 0 means the action was fine but
  // paid nothing (unconfigured, already awarded, pending approval); a negative
  // means points were taken back, which an unlike does silently.
  toast.success(earned > 0 ? `${message} +${earned} points!` : message);
}

/**
 * Announce an award only when there is one, and report whether the member's
 * total moved at all.
 *
 * Use this for actions that can legitimately pay nothing: un-liking (negative,
 * the points are handed back), re-doing something already paid for (0), or
 * editing rather than creating. The total still needs refreshing on a negative,
 * which is why the return value and the toast are decided separately.
 *
 * @returns {boolean} whether the member's points changed
 */
export function toastEarned(awarded, message) {
  const earned = Number(awarded || 0);
  if (earned > 0) toastPoints(earned, message);
  // A negative award (un-liking hands the points back) can re-lock a level
  // group, and it never reaches toastPoints — so it drops the reads here.
  else if (earned !== 0) forgetPointsDerivedReads();
  return earned !== 0;
}

/**
 * Likes credit the member who clicks like. `earner_id` is still checked against
 * the signed-in member rather than assumed — that check is what guarantees a
 * member is never told they earned points that landed in someone else's balance,
 * and it keeps working if the crediting rule is ever changed again.
 *
 * Only a positive award is announced. Un-liking returns a negative amount (the
 * points are handed back) and re-liking an item already paid for returns 0;
 * neither deserves a toast, but both still move the total, so the caller is told
 * to refresh "My Points & Badges" either way.
 *
 * @param {object} data    the like endpoint's response body
 * @param {object} member  the signed-in member
 * @returns {boolean}      whether the signed-in member's points changed
 */
export function toastLikePoints(data, member) {
  const earner = Number(data?.earner_id || 0);
  const me = Number(member?.community_member_id || 0);
  const awarded = Number(data?.points_awarded || 0);
  if (!earner || earner !== me || !awarded) return false;

  if (awarded > 0) toastPoints(awarded, "Liked.");
  return true;
}
